import { Box, Divider, useTheme } from "@mui/material";
import React, { ReactElement, useCallback } from "react";

import CustomPopover from "@convin/components/custom_components/Popover/CustomPopover";
import { Label } from "@convin/components/custom_components/Typography/Label";
import { Option } from "@convin/components/select/GenericSelect";
import TrailingSvg from "@convin/components/svg/TrailingSvg";

import useGlobalFiltersStateContext from "../../../../../../hooks/useGlobalFiltersStateContext";
import { StyledFilterBox, filterPopoverSx } from "../../../styles";
import FilterListButton from "./FilterListButton";

type ModuleOption = Option<string | null>;

export default function ModuleFilter(): ReactElement {
    const theme = useTheme();
    const { state, updateState } = useGlobalFiltersStateContext();

    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback((): void => {
        setAnchorEl(null);
    }, []);

    const handleChange = (option: ModuleOption): void => {
        updateState({
            module: option.id,
        });
        handleClose();
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const options: ModuleOption[] = [
        { id: null, value: "All" },
        // { id: "coaching", value: "Coaching" },
        // { id: "meetings", value: "Meetings" },
        { id: "audit.models.Template", value: "Template" },
        { id: "audit.models.category", value: "Category" },
        { id: "audit.models.Question", value: "Parameters" },
        { id: "gpt.models.ScoreSense", value: "Score Sense" },
        { id: "person.models.Role", value: "Role Manager" },
        { id: "audit.models.Violation ", value: "Violation Manager" },
        { id: "topic.models.Topic", value: "Topic Manager" },
        { id: "person.models.Team", value: "Team Manager" },
        { id: "event.models.ScheduleReport", value: "Schedule Report" },
        { id: "concalendar.models.Tag", value: "Tag Manager" },
        { id: "concalendar.models.CallType", value: "Type Manager" },
        { id: "audit.models.ManualQmsFields", value: "Manual QMS Fields" },
        {
            id: "customer_intelligence.models.CustomerIntelligenceTab ",
            value: "Customer Intelligence",
        },
        { id: "person.models.Person", value: "User Manager" },
        { id: "gpt.models.NERConfig", value: "Entity Manager" },
        { id: "meeting.models.Meeting", value: "Conversations" },
        { id: "gpt.models.Summary", value: "AI Summary" },
        { id: "gpt.models.Question", value: "AI Insights" },
        { id: "SamplingRule", value: "Sampling Manager" },
    ];

    return (
        <>
            <StyledFilterBox onClick={handleClick} aria-describedby={id}>
                <svg
                    width="19"
                    height="18"
                    viewBox="0 0 19 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M14.75 7.5H4.25C3.42275 7.5 2.75 8.17275 2.75 9V15C2.75 15.8273 3.42275 16.5 4.25 16.5H14.75C15.5772 16.5 16.25 15.8273 16.25 15V9C16.25 8.17275 15.5772 7.5 14.75 7.5ZM4.25 15V9H14.75L14.7515 15H4.25ZM4.25 4.5H14.75V6H4.25V4.5ZM5.75 1.5H13.25V3H5.75V1.5Z"
                        fill="white"
                    />
                    <rect x="3" y="9" width="13" height="6" fill="white" />
                </svg>

                <Label
                    isEllipses
                    hasTooltip
                    colorType="white"
                    className="flex-1 font-semiBold"
                    variant="medium"
                >
                    {options.find((option) => option.id === state.module)
                        ?.value ?? "All"}
                </Label>

                <Box {...(open && { className: "rotate-180" })}>
                    <TrailingSvg />
                </Box>
            </StyledFilterBox>
            <CustomPopover
                anchorEl={anchorEl}
                onClose={handleClose}
                sx={filterPopoverSx(theme)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Label colorType="666" variant="medium" sx={{ p: 1.5 }}>
                    Select Module
                </Label>
                <Divider />
                <Box className="flex-1">
                    {options.map((option, idx) => (
                        <FilterListButton
                            onClick={() => handleChange(option)}
                            key={idx}
                        >
                            {option.value}
                        </FilterListButton>
                    ))}
                </Box>
            </CustomPopover>
        </>
    );
}
